import { Button } from 'components/Button';
import { Grid, GridProps } from 'components/Grid/Grid';
import { ICONS } from 'components/Icon';
import { Page } from 'components/Page';
import { PageEmpty } from 'components/PageEmpty';
import { Txt } from 'components/Txt';
import { pluralise } from 'lib/pluralise';
import { theme } from 'lib/theme';
import React from 'react';
import { View } from 'react-native';
import { useDispatch } from 'react-redux';
import { EngagePropertiesHeader } from './EngagePropertiesHeader';
import {
  doEngageClearData,
  doEngagePropertiesLoad,
} from 'store/remote/engageActions';
import {
  selectEngagePropertiesItemIds,
  selectEngagePropertiesIsWaitingForInitLoad,
  selectEngagePropertiesItem,
  selectEngagePropertiesTotalCount,
} from 'store/remote/engageSelectors';
import { useSelector } from 'hooks/useSelector';
import { ReferenceToText } from 'components/ReferenceText';
import { EngageLoader } from './EngageLoader';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { NavigationStacks, Screens, Stacks } from 'components/Navigation/types';
import { EngagePropertyListItem } from 'types/Engage';

type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.Engage],
  Screens.EngageProperties
>;

type DefinedGridProps = GridProps<
  'property' | 'address' | 'noOfFlats' | 'noOfContacts',
  string,
  { pageKey: string; onRowPress: (property: EngagePropertyListItem) => void }
>;

const useGetRowData: DefinedGridProps['getRowData'] = ({ id, extraData }) => {
  const data = useSelector((state) =>
    selectEngagePropertiesItem(state, extraData.pageKey, id),
  );
  return React.useMemo(() => {
    if (!data) {
      return null;
    }
    return {
      onPress: () => extraData.onRowPress(data),
      columns: {
        property: () => {
          return (
            <View
              style={{ flex: 1, flexDirection: 'row', alignItems: 'center' }}
            >
              <View style={{ flex: 1 }}>
                <Txt numberOfLines={1} weight="bold">
                  {data.name}
                </Txt>
                <ReferenceToText
                  reference={data.reference}
                  customReference={data.customReference}
                />
              </View>
              {/* <ViewWithSeparators
                horizontal
                separatorMargin={theme.paddingHorizontalHalf / 2}
                separatorColor="transparent"
                style={{ alignItems: 'center' }}
              >
                {!!engageUrl && (
                  <IconButton
                    icon={ICONS.ArrowUpRightFromSquareLight}
                    onPress={() => openUrl(engageUrl)}
                    iconSize={12}
                    accessibilityLabel="Open engage website for this property"
                    accessibilityHint={smartText(
                      "Opens this property's engage website",
                    )}
                  />
                )}
              </ViewWithSeparators> */}
            </View>
          );
        },
        address: () => {
          const addressLines: string[] = data.postcode
            ? [...data.addressLines] // [...data.asset.addressLines, data.asset.postcode]
            : data.addressLines;
          return (
            <View
              style={{
                flex: 1,
              }}
            >
              {!!addressLines.length && (
                <Txt>
                  {!!data.parentName && (
                    <Txt weight="medium">{data.parentName}</Txt>
                  )}
                  {data.parentName && addressLines.length ? ', ' : ''}
                  {addressLines.join(', ')}
                  {addressLines.length && data.postcode ? ', ' : ''}
                  <Txt weight="medium" numberOfLines={1}>
                    {data.postcode}
                  </Txt>
                </Txt>
              )}
            </View>
          );
        },
        noOfFlats: () => (
          <Txt>
            {pluralise(
              // data.asset.noOfFlats,
              data.noOfFlats,
              '{x} Flat',
              '{x} Flats',
              'No Flats',
            )}
          </Txt>
        ),
        noOfContacts: () => (
          <Txt>
            {pluralise(
              data.noOfContacts,
              '{x} Contact',
              '{x} Contacts',
              'No Contacts',
            )}
          </Txt>
        ),
      },
    };
  }, [data, extraData]);
};

export const EngageProperties: React.FC<Props> = ({
  navigation,
  route: {
    params: { pageKey },
  },
}) => {
  const dispatch = useDispatch();

  const isWaitingInitialLoad = useSelector((state) =>
    selectEngagePropertiesIsWaitingForInitLoad(state, pageKey),
  );
  const gridIds = useSelector((state) =>
    selectEngagePropertiesItemIds(state, pageKey),
  );
  const totalCount = useSelector((state) =>
    selectEngagePropertiesTotalCount(state, pageKey),
  );
  const remainingCount = totalCount - gridIds.length;

  const [columns] = React.useState<DefinedGridProps['columns']>([
    {
      name: 'Property',
      dataIndex: 'property',
      minWidth: 200,
      flex: 1,
    },
    {
      name: 'Address',
      dataIndex: 'address',
      minWidth: 200,
      flex: 2,
    },
    {
      name: 'Flats',
      dataIndex: 'noOfFlats',
      width: 100,
    },
    {
      name: 'Contacts',
      dataIndex: 'noOfContacts',
      width: 100,
    },
  ]);

  const loadInitialised = React.useRef(false);

  const handleLoadGrid = React.useCallback(
    (page: 'reset' | 'next') => {
      loadInitialised.current = true;
      dispatch(
        doEngagePropertiesLoad({
          key: pageKey,
          page,
        }),
      );
    },
    [dispatch, pageKey],
  );

  const handleLoadMorePress = () => {
    handleLoadGrid('next');
  };

  const handleReloadPress = () => {
    handleLoadGrid('reset');
  };

  const handleRowPress = React.useCallback(
    (property: EngagePropertyListItem) => {
      navigation.navigate(Screens.EngageFlats, {
        pageKey: pageKey,
        property: property,
      });
    },
    [navigation, pageKey],
  );

  React.useEffect(() => {
    if (!loadInitialised.current) {
      loadInitialised.current = true;
      handleLoadGrid('reset');
    }
    return () => {
      dispatch(doEngageClearData({ key: pageKey }));
    };
  }, [dispatch, pageKey, handleLoadGrid]);

  return (
    <View style={{ position: 'relative', flex: 1 }}>
      <EngageLoader pageKey={pageKey} />
      <Page
        isInModal={false}
        contentPaddingTop={0}
        ignoreSafeAreaBottom
        header={
          <EngagePropertiesHeader
            onReloadPress={handleReloadPress}
            propertiesCount={totalCount}
            pageKey={pageKey}
          />
        }
      >
        {isWaitingInitialLoad ? (
          <PageEmpty
            text="Waiting..."
            style={{ backgroundColor: theme.colors.background }}
          />
        ) : !gridIds.length ? (
          <PageEmpty
            text="No Properties Found"
            subtext="Try changing your filter"
            iconCircled={false}
            style={{ backgroundColor: theme.colors.background }}
          />
        ) : (
          <Grid
            columns={columns}
            data={gridIds}
            extraData={{
              pageKey: pageKey,
              onRowPress: handleRowPress,
            }}
            getRowData={useGetRowData}
            keyExtractor={(i) => `${i}`}
            ListFooterComponent={
              remainingCount > 0 ? (
                <View
                  style={{
                    padding: theme.paddingHorizontal * 2,
                    paddingTop: theme.paddingHorizontal,
                    alignItems: 'flex-start',
                  }}
                >
                  <Button
                    text={pluralise(
                      remainingCount,
                      '{x} More Result, Load',
                      '{x} More Results, Load More',
                    )}
                    onPress={handleLoadMorePress}
                    icon={ICONS.ArrowDownLight}
                  />
                </View>
              ) : (
                <View style={{ height: theme.paddingHorizontal * 2 }} />
              )
            }
            // refreshControl,
          />
        )}
      </Page>
    </View>
  );
};
